import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PlusIcon, TrashIcon, PrinterIcon, ArchiveBoxIcon, ShoppingBagIcon, ExclamationTriangleIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { productService, FilterOptions } from '../../services/productService';
import toast from 'react-hot-toast';
import { generateProductLabelZPL } from '../../utils/zplUtils';
import { useZebraPrinter } from '../../hooks/useZebraPrinter';
import CustomCombobox from '../common/Combobox';
import { sortSizes } from '../../utils/sortUtils';

interface InvoiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialInvoiceId?: string;
    initialBrand?: string;
}

interface InvoiceItem {
    id: string; // temp id for UI
    code: string;
    color: string;
    size: string;
    price: string;
    priceUsd?: string;
    quantity: string;
    preOrder: boolean;
    inventoryType: 'maestro' | 'novias';
}

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    initialInvoiceId,
    initialBrand
}) => {
    // Global State
    // Removed globalInventoryType
    const [globalBrand, setGlobalBrand] = useState<string>('');
    const [manualInvoiceId, setManualInvoiceId] = useState<string>('');

    const [items, setItems] = useState<InvoiceItem[]>([]);
    const [loading, setLoading] = useState(false);

    // Matching Modal State
    const [matchSuggestions, setMatchSuggestions] = useState<any[]>([]);
    const [showMatchModal, setShowMatchModal] = useState(false);

    // Warning Modal State
    const [inventoryWarnings, setInventoryWarnings] = useState<any[]>([]);
    const [showWarningModal, setShowWarningModal] = useState(false);

    // Confirmation Modal State - REMOVED
    // const [confirmModalState, setConfirmModalState] = ...

    // Data Loading State - Store the full response
    const [filterData, setFilterData] = useState<FilterOptions | null>(null);

    const [processedResults, setProcessedResults] = useState<any[] | null>(null);

    // Matrix Sub-Modal State
    const [activeMatrixRowId, setActiveMatrixRowId] = useState<string | null>(null);
    const [currentColors, setCurrentColors] = useState<string[]>([]);
    const [currentSizes, setCurrentSizes] = useState<string[]>([]);
    const [quantities, setQuantities] = useState<Record<string, Record<string, number>>>({});
    const [isLoadingVariants, setIsLoadingVariants] = useState(false);
    const { 
        printLabel, 
        loading: printing,
        isUSBConnected,
        connectUSB,
        usbDeviceName,
        error: printerError
    } = useZebraPrinter();

    // Reset state on open
    useEffect(() => {
        const loadFilterOptions = async () => {
            try {
                const options = await productService.getFilterOptions();
                setFilterData(options);
            } catch (error) {
                console.error("Error loading brands", error);
                toast.error("Error cargando datos de inventario");
            }
        };

        if (isOpen) {
            loadFilterOptions();
            
            // Check for draft
            const draft = localStorage.getItem('invoice_draft');
            if (draft) {
                try {
                    const parsed = JSON.parse(draft);
                    if (window.confirm("Se detectó un invoice pendiente de captura. ¿Deseas recuperar los datos?")) {
                        setGlobalBrand(parsed.brand || '');
                        setManualInvoiceId(parsed.invoiceId || '');
                        setItems(parsed.items || []);
                        return; // Skip reset
                    } else {
                        localStorage.removeItem('invoice_draft');
                    }
                } catch (e) {
                    console.error("Error parsing draft", e);
                }
            }

            setGlobalBrand(initialBrand || '');
            setManualInvoiceId(initialInvoiceId || '');
            setItems([{
                id: Date.now().toString(),
                code: '',
                color: '',
                size: '',
                price: '',
                quantity: '1',
                preOrder: true,
                inventoryType: 'maestro'
            }]);
            setProcessedResults(null);
        }
    }, [isOpen, initialInvoiceId, initialBrand]);

    // Auto-save draft effect with debounce
    useEffect(() => {
        if (!isOpen || processedResults) return;
        
        const timer = setTimeout(() => {
            if (items.some(i => (i.code || '').trim() !== '') || globalBrand) {
                localStorage.setItem('invoice_draft', JSON.stringify({
                    brand: globalBrand,
                    invoiceId: manualInvoiceId,
                    items: items
                }));
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [items, globalBrand, manualInvoiceId, isOpen, processedResults]);

    // Derived Data Helpers
    const getCurrentInventoryData = (type: 'maestro' | 'novias') => {
        if (!filterData) return null;
        return type === 'maestro' ? filterData.maestro : filterData.novias;
    };

    const getAvailableBrands = () => {
        // Brands might differ between inventories, but we want a union or just defaulting to maestro?
        // Or if globalBrand is set, filtering isn't needed for the Brand selector itself usually.
        // Let's combine both or just use maestro as primary source for Brands if that's the pattern?
        // Actually, the user selects a Brand FIRST.
        // Let's combine brands from both lists.
        const m = filterData?.maestro?.marcas || [];
        const n = filterData?.novias?.marcas || [];
        return Array.from(new Set([...m, ...n])).sort();
    };

    const getFilteredColors = (type: 'maestro' | 'novias') => {
        const data = getCurrentInventoryData(type);
        if (!data || !globalBrand) return [];
        return Array.from(new Set(data.brandColors
            .filter(bc => bc.brand === globalBrand)
            .map(bc => bc.color)));
    };

    const getFilteredSizes = (type: 'maestro' | 'novias') => {
        const data = getCurrentInventoryData(type);
        if (!data || !globalBrand) return [];
        return Array.from(new Set(data.brandSizes
            .filter(bs => bs.brand === globalBrand)
            .map(bs => bs.size)));
    };

    const getFilteredCodes = (type: 'maestro' | 'novias') => {
        const data = getCurrentInventoryData(type);
        if (!data || !globalBrand) return [];
        return data.codeBrands
            .filter(cb => cb.brand === globalBrand)
            .map(cb => cb.code);
    };

    // Handlers
    const calculateAutoPrice = (usd: string): string => {
        if (!usd) return '';
        const val = parseFloat(usd);
        if (isNaN(val)) return '';

        const fixedRate = 21.5;
        const fixedCost = 200;
        const multiplier = 1.65;

        const raw = (val * fixedRate + fixedCost) * multiplier;
        const ceilVal = Math.ceil(raw);
        const remainder = ceilVal % 100;
        const diff = 99 - remainder;

        return (ceilVal + diff).toString();
    };

    // resetItems REMOVED because unused


    // executeSwitchInventory REMOVED

    // executeChangeBrand REMOVED - Inlined in handleBrandChange

    const openMatrixModal = async (rowId: string) => {
        const rowItem = items.find(i => i.id === rowId);
        if (!rowItem) return;

        const code = (rowItem.code || '').trim().toUpperCase();
        if (!code) {
            toast.error('Debe ingresar un CÓDIGO válido primero');
            return;
        }
        
        if (!rowItem.priceUsd || parseFloat(rowItem.priceUsd) <= 0) {
            toast.error('Debe ingresar un PRECIO USD válido primero');
            return;
        }

        setIsLoadingVariants(true);
        setActiveMatrixRowId(rowId);
        setQuantities({});
        setCurrentColors([]);
        setCurrentSizes([]);

        try {
            const data = await productService.getVariants(code, rowItem.inventoryType);
            if (data.sizes && data.sizes.length > 0) {
                setCurrentSizes(data.sizes);
            }
            if (data.colors && data.colors.length > 0) {
                setCurrentColors(data.colors);
            }
            
            // Si es un código completamente nuevo, también abrimos pero no pre-cargamos nada.
        } catch (error) {
            console.error("Error fetching variants", error);
            // Ignoramos el error para dejar que el usuario capture manualmente
        } finally {
            setIsLoadingVariants(false);
        }
    };

    const closeMatrixModal = () => {
        setActiveMatrixRowId(null);
        setQuantities({});
        setCurrentColors([]);
        setCurrentSizes([]);
    };

    const handleQuantityChange = (color: string, size: string, qtyStr: string) => {
        const val = parseInt(qtyStr, 10);
        const qty = isNaN(val) ? 0 : val;
        
        setQuantities(prev => ({
            ...prev,
            [color]: {
                ...(prev[color] || {}),
                [size]: qty
            }
        }));
    };

    const addNewColorToMatrix = () => {
        const val = window.prompt("Ingrese nuevo color:")?.trim().toUpperCase();
        if (val && !currentColors.includes(val)) setCurrentColors([...currentColors, val]);
    };

    const addNewSizeToMatrix = () => {
        const val = window.prompt("Ingrese nueva talla:")?.trim().toUpperCase();
        if (val && !currentSizes.includes(val)) setCurrentSizes([...currentSizes, val]);
    };

    const handleAddModelToSummary = () => {
        const rowItem = items.find(i => i.id === activeMatrixRowId);
        if (!rowItem) return;

        const newItems: InvoiceItem[] = [];
        
        Object.entries(quantities).forEach(([color, sizeMap]) => {
            Object.entries(sizeMap).forEach(([size, qty]) => {
                if (qty > 0) {
                    for (let i = 0; i < qty; i++) {
                        newItems.push({
                            id: Math.random().toString(36).substr(2, 9),
                            code: rowItem.code.toUpperCase(),
                            color: color,
                            size: size,
                            price: rowItem.price,
                            priceUsd: rowItem.priceUsd,
                            quantity: '1',
                            preOrder: true,
                            inventoryType: rowItem.inventoryType
                        });
                    }
                }
            });
        });

        if (newItems.length === 0) {
            toast.error("Debe ingresar al menos una cantidad mayor a 0");
            return;
        }

        // We replace the original empty-ish row (if it had no color/size/qty=1) 
        // with the new rows, OR just append the new rows.
        // Actually, if the original row had color/size, we probably just append.
        // But usually the user opens the matrix on an incomplete row.
        
        setItems(prev => {
            const index = prev.findIndex(i => i.id === activeMatrixRowId);
            if (index === -1) return prev;
            
            const item = prev[index];
            const isItemEmpty = !item.color && !item.size;
            
            let updatedItems = [...prev];
            if (isItemEmpty) {
                // Replace the empty row with the first new item, then insert the rest
                updatedItems.splice(index, 1, ...newItems);
            } else {
                // Insert after
                updatedItems.splice(index + 1, 0, ...newItems);
            }
            
            // Ensure there's always an empty row at the end
            const lastItem = updatedItems[updatedItems.length - 1];
            if (lastItem && (lastItem.code || lastItem.color || lastItem.size || lastItem.price)) {
                updatedItems.push({
                    id: Date.now().toString(),
                    code: '',
                    color: '',
                    size: '',
                    price: '',
                    priceUsd: '',
                    quantity: '1',
                    preOrder: true,
                    inventoryType: 'maestro'
                });
            }
            
            return updatedItems;
        });

        toast.success(`Añadidas ${newItems.length} prendas al invoice`);
        closeMatrixModal();
    };    const getTotalInMatrix = () => {
        let total = 0;
        Object.values(quantities).forEach(sizes => {
            Object.values(sizes).forEach(qty => {
                total += qty;
            });
        });
        return total;
    };

    const handleBrandChange = (brand: string) => {
        // Just update global brand, no complex confirmation logic for now as we don't switch inventory type globally
        setGlobalBrand(brand);
        // Note: Resetting items might be annoying if just correcting a typo in brand.
        // But logic says items depend on brand.
        // Let's keep it simple: if brand changes, maybe warn?
        // For now, direct update.
        // resetItems(); // Optional: decide if we want to wipe items.
    };

    const handleAddItem = () => {
        if (!globalBrand) {
            toast.error("Seleccione una marca primero");
            return;
        }
        setItems([
            ...items,
            {
                id: Date.now().toString(),
                code: '',
                color: '',
                size: '',
                price: '',
                priceUsd: '',
                quantity: '1',
                preOrder: true,
                inventoryType: 'maestro'
            }
        ]);
    };

    const handleRemoveItem = (id: string) => {
        if (items.length === 1) {
            setItems([{ ...items[0], code: '', color: '', size: '', price: '', priceUsd: '', quantity: '1', preOrder: true, inventoryType: 'maestro' }]);
            return;
        }
        setItems(items.filter(item => item.id !== id));
    };

    const updateItem = (id: string, field: keyof InvoiceItem, value: any) => {
        // Ensure string fields are never null
        let normalizedValue = value;
        if (['code', 'color', 'size', 'price', 'priceUsd', 'quantity'].includes(field as string)) {
            normalizedValue = value ?? '';
        }

        setItems(prevItems => {
            const index = prevItems.findIndex(i => i.id === id);
            if (index === -1) return prevItems;

            const updatedItems = prevItems.map(item => {
                // If this is the item being edited directly
                if (item.id === id) {
                    const updated = { ...item, [field]: normalizedValue };
                    if (field === 'priceUsd') {
                        const calculated = calculateAutoPrice(normalizedValue);
                        if (calculated) {
                            updated.price = calculated;
                        }
                    }
                    return updated;
                }
                
                // If this is another item but shares the exact same Product Code
                // We auto-fill prices to maintain catalog consistency
                const currentCode = (prevItems[index].code || '').trim();
                if (
                    currentCode !== '' &&
                    item.code === prevItems[index].code &&
                    (field === 'priceUsd' || field === 'price')
                ) {
                    const updated = { ...item, [field]: normalizedValue };
                    if (field === 'priceUsd') {
                        const calculated = calculateAutoPrice(normalizedValue);
                        if (calculated) {
                            updated.price = calculated;
                        }
                    }
                    return updated;
                }
                
                return item;
            });

            // Logic for auto-adding rows:
            // If we are editing the last item and it's practically filled, add a new one.
            const item = updatedItems[index];
            const isLast = index === updatedItems.length - 1;
            
            const hasCode = (item.code || '').trim() !== '';
            const hasColor = (item.color || '').trim() !== '';
            const hasSize = (item.size || '').trim() !== '';
            const isFilled = hasCode && hasColor && hasSize;

            if (isLast && isFilled && (field === 'code' || field === 'color' || field === 'size' || field === 'price')) {
                return [
                    ...updatedItems,
                    {
                        id: (Date.now() + 1).toString(),
                        code: '',
                        color: '',
                        size: '',
                        price: '',
                        priceUsd: '',
                        quantity: '1',
                        preOrder: true,
                        inventoryType: 'maestro'
                    }
                ];
            }

            return updatedItems;
        });
    };

    // Helper for keyboard navigation with "Enter to repeat" feature
    const handleKeyDown = (e: React.KeyboardEvent, index: number, field: string) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            // ── FEATURE: "Enter para repetir" ──────────────────────────────
            // If Enter is pressed on an EMPTY 'code' field (not the first row),
            // inherit the previous row's code and price, then jump to color.
            if (field === 'code' && index > 0) {
                const currentValue = (e.currentTarget as HTMLInputElement).value.trim();
                if (currentValue === '') {
                    const prevItem = items[index - 1];
                    if (prevItem && prevItem.code) {
                        setItems(prev => prev.map((it, i) =>
                            i === index
                                ? { ...it, code: prevItem.code, price: prevItem.price, priceUsd: prevItem.priceUsd || '' }
                                : it
                        ));
                        // Jump to the color input of this same row
                        setTimeout(() => {
                            const inputs = Array.from(document.querySelectorAll('.invoice-input input, input.invoice-input')) as HTMLInputElement[];
                            const currentIndex = inputs.indexOf(e.currentTarget as HTMLInputElement);
                            // Color is the next input after code
                            if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
                                inputs[currentIndex + 1].focus();
                                setTimeout(() => inputs[currentIndex + 1].select?.(), 10);
                            }
                        }, 30);
                        return;
                    }
                }
            }
            // ──────────────────────────────────────────────────────────────

            const inputs = Array.from(document.querySelectorAll('.invoice-input input, input.invoice-input')) as HTMLInputElement[];
            const currentIndex = inputs.indexOf(e.currentTarget as HTMLInputElement);

            if (currentIndex !== -1 && currentIndex < inputs.length - 1) {
                inputs[currentIndex + 1].focus();
                setTimeout(() => inputs[currentIndex + 1].select?.(), 10);
            }
        }
    };

    const handleCreateAttribute = (type: 'color' | 'size', itemId: string, invType: 'maestro' | 'novias') => {
        const promptMessage = type === 'color' ? 'Ingrese el nuevo COLOR:' : 'Ingrese la nueva TALLA:';
        const newValue = window.prompt(promptMessage);

        if (!newValue || !newValue.trim()) return;

        const normalizedValue = newValue.trim().toUpperCase();

        // Update local filter data
        if (filterData) {
            const currentData = invType === 'maestro' ? filterData.maestro : filterData.novias;
            if (currentData) {
                if (type === 'color') {
                    // Check if already exists for this brand
                    const exists = currentData.brandColors.some(bc => bc.brand === globalBrand && bc.color === normalizedValue);
                    if (!exists) {
                        currentData.brandColors.push({ brand: globalBrand, color: normalizedValue });
                        // Force update
                        setFilterData({ ...filterData });
                        toast.success(`Color ${normalizedValue} agregado a la lista`);
                    }
                } else {
                    const exists = currentData.brandSizes.some(bs => bs.brand === globalBrand && bs.size === normalizedValue);
                    if (!exists) {
                        currentData.brandSizes.push({ brand: globalBrand, size: normalizedValue });
                        // Force update
                        setFilterData({ ...filterData });
                        toast.success(`Talla ${normalizedValue} agregada a la lista`);
                    }
                }
            }
        }

        // Auto select the new value for the item
        updateItem(itemId, type, normalizedValue);
    };

    const handleSubmit = async () => {
        if (!globalBrand) {
            toast.error("La marca es obligatoria");
            return;
        }

        // Filtrado riguroso antes de cualquier acción
        const validItems = items.filter(i => 
            (i.code || '').trim() !== '' && 
            (i.color || '').trim() !== '' && 
            (i.size || '').trim() !== '' &&
            (parseFloat(i.price) > 0)
        );

        const incompleteItems = items.filter(i => 
            (i.code || '').trim() !== '' && 
            ((i.color || '').trim() === '' || (i.size || '').trim() === '')
        );

        if (incompleteItems.length > 0) {
            if (!window.confirm(`Hay ${incompleteItems.length} productos con datos incompletos (sin color o talla) que serán IGNORADOS. ¿Deseas continuar?`)) {
                return;
            }
        }

        if (validItems.length === 0) {
            toast.error("Agregue al menos un producto completo (Código, Color, Talla y Precio)");
            return;
        }

        setLoading(true);
        try {
            const itemsForCheck = validItems.map(item => ({
                cod_producto: item.code,
                marca: globalBrand,
                type: item.inventoryType
            }));

            // 1. Check for cross-inventory warnings first
            const warningsResponse = await productService.checkInventoryWarnings(itemsForCheck);
            if (warningsResponse.success && warningsResponse.data && warningsResponse.data.length > 0) {
                setInventoryWarnings(warningsResponse.data);
                setShowWarningModal(true);
            } else {
                await executeMatchCheck(validItems);
            }
        } catch (error: any) {
            console.error("Error comprobando advertencias de inventario:", error);
            await executeMatchCheck(validItems); // fallback to next step
        } finally {
            setLoading(false);
        }
    };

    const executeMatchCheck = async (validItems: InvoiceItem[]) => {
        setLoading(true);
        try {
            const itemsForCheck = validItems.map(item => ({
                code: item.code,
                cod_producto: item.code,
                marca: globalBrand,
                color: item.color,
                talla: item.size,
                type: item.inventoryType
            }));

            const matchResponse = await productService.getPreOrderMatches(itemsForCheck);
            if (matchResponse.success && matchResponse.data && matchResponse.data.length > 0) {
                setMatchSuggestions(matchResponse.data);
                setShowMatchModal(true);
            } else {
                await executeSubmit();
            }
        } catch (error: any) {
            console.error("Error verificando reservaciones:", error);
            await executeSubmit();
        } finally {
            setLoading(false);
        }
    };

    const executeSubmit = async () => {
        if (!globalBrand) {
            console.error('[InvoiceModal] executeSubmit aborted: globalBrand is empty');
            return;
        }

        // Validate items using exact same logic as handleSubmit
        const validItems = items.filter(i => 
            (i.code || '').trim() !== '' && 
            (i.color || '').trim() !== '' && 
            (i.size || '').trim() !== '' &&
            (parseFloat(i.price) > 0)
        );

        if (validItems.length === 0) {
            console.error('[InvoiceModal] executeSubmit aborted: no valid items');
            return;
        }

        setLoading(true);
        try {
            // Idempotency: Auto-generate Invoice ID if not provided, so retries send the SAME ID
            let finalInvoiceId = manualInvoiceId.trim();
            if (!finalInvoiceId && !initialInvoiceId) {
                const dateStr = new Intl.DateTimeFormat('en-CA', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                }).format(new Date()).replace(/-/g, '');
                const randomSuffix = Math.floor(1000 + Math.random() * 9000);
                finalInvoiceId = `INV-${dateStr}-${randomSuffix}`;
                setManualInvoiceId(finalInvoiceId); // Update UI to show what we generated
            }

            const payload = {
                invoiceId: finalInvoiceId || undefined,
                items: validItems.map((item) => ({
                    ...item,
                    brand: globalBrand
                })),
                append: !!initialInvoiceId
            };

            console.log('[InvoiceModal] processInvoice payload:', JSON.stringify(payload, null, 2));

            const response = await productService.processInvoice(payload);

            console.log('[InvoiceModal] processInvoice response:', response);

            if (response.success) {
                const associatedCount = response.data.filter((item: any) => item._action === 'associated_preorder').length;

                if (associatedCount > 0) {
                    toast(() => (
                        <div className="flex flex-col">
                            <span className="font-bold flex items-center gap-1.5">
                                <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />
                                Atención
                            </span>
                            <span>
                                {associatedCount} producto(s) ya existían como PRE-ORDER vendido.
                                <br />Se han asociado automáticamente al Invoice actual.
                            </span>
                        </div>
                    ), { duration: 6000 });
                }

                toast.success(`Invoice procesada: ${response.data.length} productos actualizados/creados`);
                setProcessedResults(response.data);
                localStorage.removeItem('invoice_draft'); // Clear draft on success
                setShowMatchModal(false); // Make sure it's closed
                onSuccess();
            } else {
                console.error('[InvoiceModal] processInvoice returned success=false:', response.message);
                toast.error(response.message || 'Error al procesar');
            }
        } catch (error: any) {
            console.error('[InvoiceModal] processInvoice error:', error);
            toast.error(error.response?.data?.message || error.message || 'Error desconocido');
        } finally {
            setLoading(false);
        }
    };

    const handlePrintInvoice = async () => {
        if (!processedResults || processedResults.length === 0) return;

        const validItems = items.filter(i => (i.code || '').trim() !== '');

        let totalZpl = '';

        for (let i = 0; i < processedResults.length; i++) {
            const product = processedResults[i];
            const inputItem = validItems[i];
            const qty = inputItem ? (parseInt(inputItem.quantity) || 1) : 1;

            if (product) {
                const baseZpl = generateProductLabelZPL(product, (product as any).saleFolio);
                const zplWithQty = baseZpl.replace('^XZ', `^PQ${qty}^XZ`);
                totalZpl += zplWithQty;
            }
        }

        if (!totalZpl) {
            toast.error('No hay etiquetas para generar');
            return;
        }

        const success = await printLabel(totalZpl);
        if (success) {
            toast.success('Enviado a imprimir toda la invoice');
        } else {
            toast.error(printerError || 'Error al enviar a imprimir. Verifica la configuración USB.');
        }
    };

    if (!isOpen) return null;

    // Theme logic REMOVED - unused


    return createPortal(
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[1000] flex items-center justify-center p-4 transition-opacity duration-300" onClick={onClose}>
            <div className={`bg-white rounded-2xl shadow-2xl w-full max-w-[90rem] max-h-[92vh] flex flex-col overflow-hidden border border-gray-100 transform transition-all duration-300 scale-100 ring-1 ring-black/5 ring-blue-100`} onClick={(e) => e.stopPropagation()}>

                {/* Header Area */}
                <div className="bg-white border-b border-gray-100">
                    <div className="px-8 py-5 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg bg-blue-50 text-blue-600`}>
                                <ShoppingBagIcon className="w-6 h-6" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Nueva Invoice</h2>
                                <p className="text-sm text-gray-500">Registre la entrada de mercancía al sistema</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Global Controls - Enhanced UI */}
                    <div className="px-8 pb-6 flex items-end gap-6 animate-fade-in-down">

                        {/* Inventory Type Selector Removed */}

                        {/* 2. Manual Invoice ID */}
                        <div className="w-48">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                                Folio de Invoice
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={manualInvoiceId}
                                    onChange={(e) => setManualInvoiceId(e.target.value.toUpperCase())}
                                    placeholder="  Ingresar folio"
                                    disabled={!!initialInvoiceId}
                                    className={`block w-full rounded-xl border-gray-300 bg-gray-50 focus:bg-white focus:border-indigo-500 focus:ring-indigo-500 text-sm font-mono uppercase h-[3.25rem] ${initialInvoiceId ? 'cursor-not-allowed opacity-75' : ''}`}
                                />
                            </div>
                        </div>

                        {/* 3. Brand Selector */}
                        <div className="flex-1 animate-fade-in">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-between">
                                <span>Marca de la Invoice</span>
                                {!globalBrand && (
                                    <span className="text-[10px] bg-red-50 text-red-500 px-2 py-0.5 rounded border border-red-100 font-normal normal-case tracking-normal">
                                        Requerido
                                    </span>
                                )}
                            </label>
                            <div className="relative group">
                                <CustomCombobox
                                    value={globalBrand}
                                    onChange={handleBrandChange}
                                    options={getAvailableBrands()}
                                    placeholder="Seleccione o escriba Marca..."
                                    allowCustom={true}
                                    disabled={!!initialBrand}
                                    className={`transition-colors text-base [&>div>div]:h-[3.25rem] [&>div>div]:rounded-xl [&>div>div]:flex [&>div>div]:items-center [&>div>div]:border-2 [&>div>div]:border-blue-200 [&>div>div]:focus-within:border-blue-500 ${initialBrand ? '[&>div>div]:bg-gray-50 [&>div>div]:cursor-not-allowed opacity-75' : ''}`}
                                />

                            </div>
                        </div>

                        {/* Info Badge */}
                        {globalBrand && (
                            <div className={`px-4 py-2 rounded-lg text-sm border flex items-center animate-fade-in h-[3.25rem] bg-blue-50 border-blue-100 text-blue-700`}>
                                <ShoppingBagIcon className="mr-2 w-5 h-5 opacity-50" />
                                <div>
                                    <span className="font-bold block text-xs uppercase opacity-70">Marca Seleccionada</span>
                                    <span className="font-semibold">{globalBrand}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-auto bg-gray-50/50 relative scrollbar-thin scrollbar-thumb-gray-300">
                    {!globalBrand ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 p-10 animate-fade-in">
                            <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 bg-white shadow-sm border border-blue-100 text-blue-100`}>
                                <ArchiveBoxIcon className={`w-12 h-12 text-blue-200`} />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900">Seleccione una Marca</h3>
                            <p className="text-gray-500 mt-2 text-center max-w-sm">
                                Para mantener el orden, todos los productos de una invoice deben pertenecer a la misma marca.
                            </p>
                            <p className="text-sm mt-4 text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                                Los atributos (tallas, colores) se filtrarán automáticamente.
                            </p>
                        </div>
                    ) : (
                        <div className="min-w-full inline-block align-middle">
                            <table className="min-w-full divide-y divide-gray-200/60">
                                <thead className="bg-gray-50/80 backdrop-blur sticky top-0 z-10">
                                    <tr>
                                        {[
                                            { label: 'TIPO', width: 'w-40' },
                                            { label: 'CÓDIGO / MODELO', width: 'w-48' },
                                            { label: 'COLOR', width: 'w-48' },
                                            { label: 'TALLA', width: 'w-32' },
                                            { label: 'PRECIO USD', width: 'w-32' },
                                            { label: 'PRECIO UNIT.', width: 'w-32' },
                                            { label: 'CANTIDAD', width: 'w-24' },
                                            { label: '', width: 'w-12' }
                                        ].map((th, idx) => (
                                            <th key={idx} className={`${th.width} px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider`}>
                                                {th.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-100">
                                    {items.map((item, index) => (
                                        <tr key={item.id} className="group hover:bg-gray-50 transition-colors animate-fade-in">
                                            <td className="px-6 py-3 align-top">
                                                <div className="w-full">
                                                    <select
                                                        value={item.inventoryType}
                                                        onChange={(e) => updateItem(item.id, 'inventoryType', e.target.value as 'maestro' | 'novias')}
                                                        className={`block w-full rounded-lg border border-gray-200 py-2 pl-3 pr-10 text-sm leading-5 font-bold focus:ring-2 focus:ring-inset focus:outline-none bg-white ${item.inventoryType === 'maestro'
                                                            ? 'text-blue-700 ring-blue-200 focus:ring-blue-500'
                                                            : 'text-pink-700 ring-pink-200 focus:ring-pink-500'
                                                            }`}
                                                    >
                                                        <option value="maestro">GENERAL</option>
                                                        <option value="novias">NOVIAS</option>
                                                    </select>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 align-top">
                                                <div className="flex items-center">
                                                    <span className="text-gray-300 text-xs font-mono w-6 text-right mr-2 mt-2">{index + 1}</span>
                                                    <div className="flex-1">
                                                        <CustomCombobox
                                                            value={item.code}
                                                            onChange={(val) => updateItem(item.id, 'code', val.toUpperCase())}
                                                            options={getFilteredCodes(item.inventoryType)}
                                                            placeholder={
                                                                index > 0 && items[index - 1]?.code && !item.code
                                                                    ? `↵ Repetir: ${items[index - 1].code}`
                                                                    : 'CÓDIGO...'
                                                            }
                                                            allowCustom={true}
                                                            className="font-mono text-sm uppercase invoice-input"
                                                            onKeyDown={(e) => handleKeyDown(e, index, 'code')}
                                                        />
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 align-top">
                                                <div className="flex space-x-2">
                                                    <div className="flex-1">
                                                        <CustomCombobox
                                                            value={item.color}
                                                            onChange={(val) => updateItem(item.id, 'color', val)}
                                                            options={getFilteredColors(item.inventoryType)}
                                                            placeholder="Color..."
                                                            allowCustom={true}
                                                            className="invoice-input"
                                                            onKeyDown={(e) => handleKeyDown(e, index, 'color')}
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={() => handleCreateAttribute('color', item.id, item.inventoryType)}
                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200"
                                                        title="Crear nuevo color"
                                                        tabIndex={-1}
                                                    >
                                                        <PlusIcon className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 align-top">
                                                <div className="flex space-x-2">
                                                    <div className="flex-1">
                                                        <CustomCombobox
                                                            value={item.size}
                                                            onChange={(val) => updateItem(item.id, 'size', val.toUpperCase())}
                                                            options={getFilteredSizes(item.inventoryType)}
                                                            placeholder="Talla"
                                                            allowCustom={true}
                                                            className="uppercase invoice-input"
                                                            onKeyDown={(e) => handleKeyDown(e, index, 'size')}
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={() => handleCreateAttribute('size', item.id, item.inventoryType)}
                                                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-gray-200"
                                                        title="Crear nueva talla"
                                                        tabIndex={-1}
                                                    >
                                                        <PlusIcon className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>

                                            <td className="px-6 py-3 align-top">
                                                <div className="relative rounded-md shadow-sm">
                                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                        <span className="text-gray-500 sm:text-sm">USD</span>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        value={item.priceUsd || ''}
                                                        placeholder="0.00"
                                                        className="block w-full rounded-md border-gray-300 bg-white pl-10 pr-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 font-medium text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none shadow-sm invoice-input"
                                                        onChange={(e) => updateItem(item.id, 'priceUsd', e.target.value)}
                                                        onKeyDown={(e) => handleKeyDown(e, index, 'priceUsd')}
                                                    />
                                                </div>
                                            </td>

                                            <td className="px-6 py-3 align-top">
                                                <div className="relative rounded-md shadow-sm">
                                                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                        <span className="text-gray-500 sm:text-sm">$</span>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        value={item.price}
                                                        placeholder="0.00"
                                                        className="block w-full rounded-md border-gray-300 bg-white pl-7 pr-4 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 font-medium text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none shadow-sm invoice-input"
                                                        onChange={(e) => updateItem(item.id, 'price', e.target.value)}
                                                        onKeyDown={(e) => handleKeyDown(e, index, 'price')}
                                                    />
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 align-top">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    className="block w-full rounded-md border-gray-300 bg-white py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 text-center font-bold shadow-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none invoice-input"
                                                    onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                                                    onKeyDown={(e) => handleKeyDown(e, index, 'quantity')}
                                                />
                                            </td>
                                            <td className="px-6 py-3 align-top text-center">
                                                <div className="flex items-center justify-center space-x-1">
                                                    <button
                                                        onClick={() => openMatrixModal(item.id)}
                                                        className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all mt-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100"
                                                        title="Añadir múltiples variantes (Matriz)"
                                                    >
                                                        <TableCellsIcon className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleRemoveItem(item.id)}
                                                        className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all mt-0.5 opacity-0 group-hover:opacity-100 focus:opacity-100"
                                                        title="Eliminar fila"
                                                    >
                                                        <TrashIcon className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <div className="p-4 border-t border-gray-100 bg-gray-50">
                                <button
                                    onClick={handleAddItem}
                                    className={`flex items-center justify-center w-full py-3 border-2 border-dashed rounded-xl transition-all font-semibold border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300`}
                                >
                                    <PlusIcon className="w-5 h-5 mr-2" />
                                    Agregar otra línea
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Action Area */}
                <div className="p-6 border-t border-gray-100 bg-white z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)]">
                    {processedResults ? (
                        <div className="flex items-center justify-between animate-fade-in bg-green-50 p-4 rounded-xl border border-green-100">
                            <div className="flex items-center text-green-700">
                                <div className="bg-green-100 p-2 rounded-full mr-3">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                </div>
                                <div>
                                    <h4 className="font-bold">¡Invoice procesada correctamente!</h4>
                                    <p className="text-sm opacity-80">{processedResults.length} productos registrados con Folio: <strong>{manualInvoiceId || 'Auto-generado'}</strong></p>
                                </div>
                            </div>
                            <div className="flex space-x-3">
                                <button
                                    onClick={onClose}
                                    className="text-gray-600 hover:text-gray-900 font-medium px-5 py-2.5 rounded-lg hover:bg-gray-100 transition-colors"
                                >
                                    Cerrar
                                </button>
                                <button
                                    onClick={connectUSB}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                                      isUSBConnected 
                                        ? 'bg-green-50 border-green-500 text-green-700' 
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                    title={isUSBConnected ? `Conectado a ${usbDeviceName}` : 'Configurar Impresora Zebra via USB'}
                                >
                                    <PrinterIcon className="w-5 h-5" />
                                    <span>{isUSBConnected ? 'USB Listo' : 'Configurar USB'}</span>
                                </button>
                                <button
                                    onClick={handlePrintInvoice}
                                    disabled={printing}
                                    className="flex items-center space-x-2 bg-gray-900 text-white px-5 py-2.5 rounded-lg hover:bg-gray-800 transition-all font-medium shadow-lg hover:shadow-xl active:scale-95 disabled:opacity-70"
                                >
                                    <PrinterIcon className="w-5 h-5" />
                                    <span>{printing ? 'Generando...' : 'Imprimir Etiquetas'}</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex justify-between items-center">
                            <div className="text-sm text-gray-500 italic">
                                * Verifique los datos antes de procesar.
                            </div>
                            <div className="flex space-x-4">
                                <button
                                    onClick={onClose}
                                    className="px-6 py-2.5 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 font-medium transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading || !globalBrand}
                                    className={`px-8 py-2.5 text-white rounded-lg transition-all font-bold shadow-md hover:shadow-lg active:scale-95 flex items-center ${loading || !globalBrand
                                        ? 'bg-gray-300 cursor-not-allowed shadow-none'
                                        : 'bg-blue-600 hover:bg-blue-700 shadow-blue-200'
                                        }`}
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Procesando...
                                        </>
                                    ) : (
                                        <>
                                            <span>Procesar Entrada</span>
                                            <span className="ml-2 opacity-70 border-l border-white/20 pl-2 text-xs font-normal">
                                                {items.length} ítems
                                            </span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
                {/* MATRIX SUB-MODAL */}
                {activeMatrixRowId && (
                    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[1050] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden animate-fade-in-up">
                            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Múltiples Variantes</h3>
                                    <p className="text-sm text-gray-500">
                                        Código: <span className="font-mono font-bold text-indigo-600">{items.find(i => i.id === activeMatrixRowId)?.code}</span>
                                    </p>
                                </div>
                                <button onClick={closeMatrixModal} className="p-2 text-gray-400 hover:bg-gray-200 rounded-full">
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>
                            
                            <div className="p-6 flex-1 overflow-auto">
                                {isLoadingVariants ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                        <span className="ml-3 text-gray-500">Cargando variantes...</span>
                                    </div>
                                ) : (
                                    <div className="border border-gray-200 rounded-xl overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider bg-white sticky left-0 z-10 border-r border-gray-200 w-32 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                        <div className="flex justify-between items-center">
                                                            TALLA ↘
                                                            <button 
                                                                onClick={addNewSizeToMatrix}
                                                                className="p-1 hover:bg-gray-200 rounded text-indigo-600"
                                                                title="Agregar nueva talla"
                                                            >
                                                                <PlusIcon className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </th>
                                                    {sortSizes(currentSizes).map(size => (
                                                        <th key={size} className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider min-w-[80px]">
                                                            {size}
                                                        </th>
                                                    ))}
                                                    {currentSizes.length === 0 && (
                                                        <th className="px-4 py-3 text-center text-xs text-gray-400 italic">No hay tallas</th>
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {currentColors.map(color => (
                                                    <tr key={color} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-700 bg-white sticky left-0 z-10 border-r border-gray-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                                                            {color}
                                                        </td>
                                                        {sortSizes(currentSizes).map(size => {
                                                            const qty = quantities[color]?.[size] || '';
                                                            return (
                                                                <td key={`${color}-${size}`} className="px-2 py-2">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={qty}
                                                                        onChange={(e) => handleQuantityChange(color, size, e.target.value)}
                                                                        className={`w-full text-center border-gray-300 rounded-lg shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm font-bold ${qty ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-gray-50/50 hover:bg-white text-gray-500'}`}
                                                                        placeholder="-"
                                                                        onFocus={(e) => e.target.select()}
                                                                    />
                                                                </td>
                                                            );
                                                        })}
                                                        {currentSizes.length === 0 && <td className="px-4 py-3"></td>}
                                                    </tr>
                                                ))}
                                                {currentColors.length === 0 && (
                                                    <tr>
                                                        <td colSpan={currentSizes.length + 1} className="px-4 py-8 text-center text-gray-500">
                                                            No hay colores definidos. Haz clic en "Agregar Color" abajo.
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        
                                        <div className="p-3 bg-gray-50 border-t border-gray-200 flex justify-center">
                                            <button 
                                                onClick={addNewColorToMatrix}
                                                className="flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                                            >
                                                <PlusIcon className="w-4 h-4 mr-1" />
                                                Agregar Fila de Color
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
                                <div className="text-sm text-gray-600">
                                    Total de prendas a añadir: <span className="font-bold text-lg text-indigo-600 ml-1">{getTotalInMatrix()}</span>
                                </div>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={closeMatrixModal}
                                        className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={handleAddModelToSummary}
                                        className="px-6 py-2 border border-transparent shadow-sm text-sm font-medium rounded-xl text-white bg-indigo-600 hover:bg-indigo-700"
                                    >
                                        Añadir al Invoice
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}


            {/* Warning Modal UI - Rendered independently so it works regardless of processedResults state */}
            {showWarningModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => !loading && setShowWarningModal(false)} />
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col overflow-hidden relative animate-bounce-in" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-amber-50 border-b border-amber-100 p-6 flex justify-between items-center">
                            <div className="flex items-center">
                                <ExclamationTriangleIcon className="w-8 h-8 text-amber-500 mr-3" />
                                <div>
                                    <h3 className="text-xl font-bold text-amber-900">Advertencias de Inventario</h3>
                                    <p className="text-sm text-amber-700 mt-1">
                                        Hemos detectado posibles inconsistencias en el tipo de inventario seleccionado.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 bg-gray-50 flex-1 max-h-[60vh] overflow-y-auto space-y-4">
                            {inventoryWarnings.map((warning, i) => (
                                <div key={i} className="bg-white border-l-4 border-amber-500 rounded-r-lg p-4 shadow-sm flex items-start">
                                    <div className="mr-3 mt-0.5 text-amber-500">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                                    </div>
                                    <div>
                                        <div className="font-bold font-mono text-gray-900">{warning.code}</div>
                                        <p className="text-sm text-gray-600 mt-1">{warning.message}</p>
                                    </div>
                                </div>
                            ))}
                            <div className="bg-blue-50 text-blue-800 p-4 rounded-lg mt-6 text-sm">
                                <strong>¿Qué hacer?</strong><br />
                                Si seleccionaste &quot;NOVIAS&quot; por error pero el producto es GENERAL (o viceversa), cancela aquí y corrige el selector en el invoice.<br />
                                Si realmente deseas ingresarlo a este inventario, puedes ignorar esta advertencia.
                            </div>
                        </div>
                        <div className="bg-white border-t border-gray-200 p-5 flex justify-end gap-3">
                            <button
                                onClick={() => setShowWarningModal(false)}
                                disabled={loading}
                                className="px-5 py-2 text-gray-700 font-bold hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 disabled:opacity-50"
                            >
                                Cancelar y Corregir
                            </button>
                            <button
                                onClick={() => {
                                    const validItems = items.filter(i => (i.code || '').trim() !== '');
                                    if (validItems.length > 0) {
                                        setShowWarningModal(false);
                                        executeMatchCheck(validItems);
                                    }
                                }}
                                disabled={loading}
                                className="px-6 py-2 bg-amber-600 text-white font-bold rounded-lg hover:bg-amber-700 transition-all shadow-md active:scale-95 disabled:opacity-50"
                            >
                                Ignorar advertencia y Continuar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Auto Match Modal UI */}
            {showMatchModal && (
                <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => !loading && setShowMatchModal(false)} />
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col overflow-hidden relative animate-bounce-in" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-indigo-50 border-b border-indigo-100 p-6 flex items-center">
                            <div>
                                <h3 className="text-xl font-bold text-indigo-900">Pre-orders detectados</h3>
                            </div>
                        </div>
                        <div className="p-6 bg-gray-50 flex-1 flex flex-col items-center text-center space-y-4">
                            <p className="text-lg text-gray-700">
                                Se detectaron <strong className="text-indigo-600 text-2xl">{matchSuggestions.reduce((acc, curr) => acc + (curr.matches?.length || 0), 0)}</strong> Pre-orders coincidentes.
                            </p>
                            <p className="text-md text-gray-600">
                                Serán vinculados automáticamente a este Invoice en orden de antigüedad. Si las unidades del Invoice superan los pre-orders, el excedente ingresará como stock regular.
                            </p>
                        </div>
                        <div className="bg-white border-t border-gray-200 p-5 flex justify-end gap-3">
                            <button
                                onClick={() => setShowMatchModal(false)}
                                disabled={loading}
                                className="px-5 py-2 text-gray-600 hover:bg-gray-100 font-medium rounded-lg transition-colors border border-gray-300 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => executeSubmit()}
                                disabled={loading}
                                className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95 flex items-center gap-2 disabled:opacity-50"
                            >
                                {loading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                                {loading ? 'Procesando...' : 'Confirmar y Procesar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>,
        document.body
    );
};
